import { useEffect, useId, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { Modal } from './Modal';
import type { ConfirmDialogVariant } from './ConfirmDialog';

export interface PromptDialogProps {
  isOpen: boolean;
  title?: string;
  message: ReactNode;
  defaultValue?: string;
  placeholder?: string;
  inputLabel?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
  /** Minimum length required to submit (0 = empty allowed). Ignored when expectedValue is set. */
  minLength?: number;
  maxLength?: number;
  /**
   * When set, confirm stays disabled until the input matches exactly.
   * Used for typed delete / high-risk confirmations.
   */
  expectedValue?: string;
  isLoading?: boolean;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

/**
 * Shared prompt dialog — replaces browser window.prompt for in-app UX.
 */
export function PromptDialog({
  isOpen,
  title = '请输入',
  message,
  defaultValue = '',
  placeholder,
  inputLabel,
  confirmLabel = '确定',
  cancelLabel = '取消',
  variant = 'primary',
  minLength = 0,
  maxLength,
  expectedValue,
  isLoading = false,
  onConfirm,
  onCancel,
}: PromptDialogProps) {
  const inputId = useId();
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
    }
  }, [isOpen, defaultValue]);

  const trimmed = value.trim();
  const requiresExactMatch = expectedValue != null;
  const matchesExpected = !requiresExactMatch || value === expectedValue;
  const tooShort = !requiresExactMatch && trimmed.length < minLength;
  const tooLong = maxLength != null && value.length > maxLength;
  const canSubmit = matchesExpected && !tooShort && !tooLong && !isLoading;

  const submit = () => {
    if (!canSubmit) return;
    onConfirm(value);
  };

  const inputError = (() => {
    if (requiresExactMatch && value.length > 0 && !matchesExpected) {
      return '需与确认文本完全一致';
    }
    if (tooShort && trimmed.length > 0) {
      return `至少需要 ${minLength} 个字符`;
    }
    if (tooLong) {
      return `不能超过 ${maxLength} 个字符`;
    }
    return undefined;
  })();

  return (
    <Modal
      isOpen={isOpen}
      onClose={isLoading ? () => undefined : onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={submit}
            disabled={!canSubmit}
            isLoading={isLoading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="text-sm leading-6 text-slate-600 whitespace-pre-line">{message}</div>
        <Input
          id={inputId}
          label={inputLabel}
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          autoFocus
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
          error={inputError}
        />
      </div>
    </Modal>
  );
}
